import { useState } from 'react';
import { put } from '../api';

// Reusable UI primitives (kept local to match the app's styling)
function Btn({ onClick, children, variant = 'primary', small, disabled, style = {} }) {
  const styles = {
    primary: { background: '#c9a96e', color: '#1a1208', border: 'none' },
    secondary: { background: 'transparent', color: '#8a7055', border: '1px solid #3a2a12' },
    danger: { background: '#e63c3c22', color: '#e63c3c', border: '1px solid #e63c3c44' },
    ghost: { background: '#c9a96e18', color: '#c9a96e', border: '1px solid #c9a96e44' },
  };
  return <button onClick={onClick} disabled={disabled} style={{ ...styles[variant], borderRadius: 8, padding: small ? '6px 14px' : '10px 22px', fontWeight: 700, cursor: disabled ? 'default' : 'pointer', fontSize: small ? 12 : 14, opacity: disabled ? 0.5 : 1, fontFamily: 'inherit', ...style }}>{children}</button>;
}
function Modal({ children, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 14, padding: 28, maxWidth: 820, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}
const inputStyle = { width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
const labelStyle = { display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' };

// Friendly labels for each section key
const SECTION_LABELS = {
  overview: 'Overview', handsOn: 'Hands-On Requirement', warningStandard: 'Warning Standard', incidentReport: 'Incident Report Guidance',
  keyPoints: 'Key Points', videoSOPs: 'Video SOPs', startup: 'Startup Steps', shutdown: 'Shutdown Steps', closing: 'Closing Steps',
  checklist: 'Checklist', immediateManagerCases: 'Immediate Manager Cases', unacceptableBehavior: 'Unacceptable Behavior', foodSafety: 'Food Safety',
  scripts: 'Scripts', systems: 'Systems', troubleshooting: 'Troubleshooting', stanceGuide: 'Stance & Technique Guide',
};
// Which shape each section uses
const TEXT_SECTIONS = ['overview', 'handsOn', 'warningStandard', 'incidentReport'];
const STRLIST_SECTIONS = ['keyPoints', 'videoSOPs', 'startup', 'shutdown', 'closing', 'checklist', 'immediateManagerCases', 'unacceptableBehavior', 'foodSafety'];
const OBJLIST_SECTIONS = { scripts: ['label', 'text'], systems: ['name', 'use'], troubleshooting: ['issue', 'steps'] };
const DICT_SECTIONS = ['stanceGuide'];

// All possible sections, in display order
const ALL_SECTIONS = ['overview', 'keyPoints', 'scripts', 'systems', 'stanceGuide', 'checklist', 'startup', 'shutdown', 'closing', 'troubleshooting', 'immediateManagerCases', 'unacceptableBehavior', 'warningStandard', 'incidentReport', 'foodSafety', 'videoSOPs', 'handsOn'];

// ── Text section editor ───────────────────────────────────────────────────────
function TextEditor({ value, onChange }) {
  return <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={4} style={{ ...inputStyle, resize: 'vertical' }} />;
}

// ── List-of-strings editor ────────────────────────────────────────────────────
function StrListEditor({ value, onChange }) {
  const list = Array.isArray(value) ? value : [];
  function update(i, v) { onChange(list.map((x, idx) => idx === i ? v : x)); }
  function add() { onChange([...list, '']); }
  function remove(i) { onChange(list.filter((_, idx) => idx !== i)); }
  function move(i, dir) {
    const j = i + dir; if (j < 0 || j >= list.length) return;
    const copy = [...list];[copy[i], copy[j]] = [copy[j], copy[i]]; onChange(copy);
  }
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {list.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <span style={{ color: '#5a4030', fontSize: 12, paddingTop: 10, width: 18 }}>{i + 1}.</span>
          <textarea value={item} onChange={e => update(i, e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <button onClick={() => move(i, -1)} style={{ ...inputStyle, width: 28, padding: 2, cursor: 'pointer' }}>↑</button>
            <button onClick={() => move(i, 1)} style={{ ...inputStyle, width: 28, padding: 2, cursor: 'pointer' }}>↓</button>
          </div>
          <Btn small variant="danger" onClick={() => remove(i)} style={{ marginTop: 2 }}>✕</Btn>
        </div>
      ))}
      <Btn small variant="secondary" onClick={add}>+ Add item</Btn>
    </div>
  );
}

// ── List-of-objects editor (scripts/systems/troubleshooting) ──────────────────
function ObjListEditor({ value, onChange, fields }) {
  const list = Array.isArray(value) ? value : [];
  function update(i, key, v) { onChange(list.map((x, idx) => idx === i ? { ...x, [key]: v } : x)); }
  function add() { const blank = {}; fields.forEach(f => blank[f] = f === 'steps' ? [] : ''); onChange([...list, blank]); }
  function remove(i) { onChange(list.filter((_, idx) => idx !== i)); }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {list.map((item, i) => (
        <div key={i} style={{ background: '#2a1f0f', borderRadius: 8, padding: 12, border: '1px solid #3a2a12' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#8a7055', fontSize: 11 }}>Item {i + 1}</span>
            <Btn small variant="danger" onClick={() => remove(i)}>Remove</Btn>
          </div>
          {fields.map(f => (
            <div key={f} style={{ marginBottom: 8 }}>
              <label style={labelStyle}>{f === 'steps' ? 'Steps (one per line)' : f}</label>
              {f === 'steps'
                ? <textarea value={(item.steps || []).join('\n')} onChange={e => update(i, 'steps', e.target.value.split('\n'))} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                : <textarea value={item[f] || ''} onChange={e => update(i, f, e.target.value)} rows={f === 'text' ? 2 : 1} style={{ ...inputStyle, resize: 'vertical' }} />}
            </div>
          ))}
        </div>
      ))}
      <Btn small variant="secondary" onClick={add}>+ Add item</Btn>
    </div>
  );
}

// ── Dict editor (stanceGuide: key/value pairs) ────────────────────────────────
function DictEditor({ value, onChange }) {
  const obj = value && typeof value === 'object' ? value : {};
  const entries = Object.entries(obj);
  function updateVal(k, v) { onChange({ ...obj, [k]: v }); }
  function updateKey(oldK, newK) {
    const next = {}; Object.entries(obj).forEach(([k, v]) => { next[k === oldK ? newK : k] = v; }); onChange(next);
  }
  function add() { onChange({ ...obj, ['newField' + (entries.length + 1)]: '' }); }
  function remove(k) { const next = { ...obj }; delete next[k]; onChange(next); }
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ background: '#2a1f0f', borderRadius: 8, padding: 10, border: '1px solid #3a2a12' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input value={k} onChange={e => updateKey(k, e.target.value)} style={{ ...inputStyle, fontWeight: 700 }} placeholder="Field name" />
            <Btn small variant="danger" onClick={() => remove(k)}>✕</Btn>
          </div>
          <textarea value={v} onChange={e => updateVal(k, e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
      ))}
      <Btn small variant="secondary" onClick={add}>+ Add field</Btn>
    </div>
  );
}

// ── Main content editor modal ─────────────────────────────────────────────────
export function ContentSectionEditor({ module, onClose, onSave, userRole }) {
  const [content, setContent] = useState(() => JSON.parse(JSON.stringify(module.content || {})));
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [addSection, setAddSection] = useState('');

  function setSection(key, val) { setContent(p => ({ ...p, [key]: val })); }
  function removeSection(key) { setContent(p => { const n = { ...p }; delete n[key]; return n; }); }

  // Sections currently present (non-empty or explicitly added)
  const present = ALL_SECTIONS.filter(s => content[s] !== undefined);
  const absent = ALL_SECTIONS.filter(s => content[s] === undefined);

  function renderEditor(key) {
    if (TEXT_SECTIONS.includes(key)) return <TextEditor value={content[key]} onChange={v => setSection(key, v)} />;
    if (STRLIST_SECTIONS.includes(key)) return <StrListEditor value={content[key]} onChange={v => setSection(key, v)} />;
    if (OBJLIST_SECTIONS[key]) return <ObjListEditor value={content[key]} onChange={v => setSection(key, v)} fields={OBJLIST_SECTIONS[key]} />;
    if (DICT_SECTIONS.includes(key)) return <DictEditor value={content[key]} onChange={v => setSection(key, v)} />;
    return <p style={{ color: '#e63c3c', fontSize: 12 }}>Unknown section type.</p>;
  }

  function addNewSection() {
    if (!addSection) return;
    let blank = '';
    if (STRLIST_SECTIONS.includes(addSection)) blank = [];
    else if (OBJLIST_SECTIONS[addSection]) blank = [];
    else if (DICT_SECTIONS.includes(addSection)) blank = {};
    setSection(addSection, blank);
    setAddSection('');
  }

  async function save() {
    setLoading(true); setMsg('');
    try {
      const res = await put(`/modules/${module.id}`, {
        title: module.title, description: module.description, content,
        pass_mark: module.pass_mark, status: module.status, hands_on_required: module.hands_on_required, min_role: module.min_role,
      });
      setMsg(res && res.direct === false ? '✓ Submitted for owner approval' : '✓ Content saved');
      setTimeout(() => { onSave(); onClose(); }, 1200);
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setLoading(false); }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 6 }}>Edit Content: {module.title}</h3>
      {userRole !== 'owner' && <p style={{ color: '#f59e0b', fontSize: 12, marginBottom: 16 }}>⚠ Your changes will be submitted for Kevin's approval before going live.</p>}

      {present.map(key => (
        <div key={key} style={{ marginBottom: 22, borderBottom: '1px solid #2a1f0f', paddingBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ color: '#c9a96e', fontWeight: 700, fontSize: 14 }}>{SECTION_LABELS[key] || key}</span>
            <Btn small variant="secondary" onClick={() => removeSection(key)}>Remove section</Btn>
          </div>
          {renderEditor(key)}
        </div>
      ))}

      {absent.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 18 }}>
          <select value={addSection} onChange={e => setAddSection(e.target.value)} style={{ ...inputStyle, width: 'auto', flex: 1 }}>
            <option value="">+ Add a section…</option>
            {absent.map(s => <option key={s} value={s}>{SECTION_LABELS[s] || s}</option>)}
          </select>
          <Btn small onClick={addNewSection} disabled={!addSection}>Add</Btn>
        </div>
      )}

      {msg && <p style={{ color: msg.startsWith('Error') ? '#e63c3c' : '#10b981', fontSize: 13, marginBottom: 12 }}>{msg}</p>}
      <div style={{ display: 'flex', gap: 10 }}>
        <Btn onClick={save} disabled={loading}>{userRole === 'owner' ? 'Save Content' : 'Submit for Approval'}</Btn>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}
